import { useState } from 'react';

export type FabCorner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

export const FAB_CORNER_STORAGE_KEY = 'cio-agent-fab-corner';

const CORNERS: FabCorner[] = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];

/** Menu opens away from the corner the launcher is parked in. */
export const CORNER_POPOVER_PLACEMENT = {
  'bottom-right': 'top-end',
  'bottom-left': 'top-start',
  'top-right': 'bottom-end',
  'top-left': 'bottom-start',
} as const satisfies Record<FabCorner, string>;

/** Quadrant of `area` the point falls in; midpoints resolve to bottom/right. */
export function nearestCorner(point: { x: number; y: number }, area: { width: number; height: number }): FabCorner {
  const isLeft = point.x < area.width / 2;
  const isTop = point.y < area.height / 2;
  if (isTop) {
    return isLeft ? 'top-left' : 'top-right';
  }
  return isLeft ? 'bottom-left' : 'bottom-right';
}

export function readStoredCorner(): FabCorner {
  try {
    const value = window.localStorage.getItem(FAB_CORNER_STORAGE_KEY);
    const corner = CORNERS.find((candidate) => candidate === value);
    return corner ?? 'bottom-right';
  } catch {
    // Storage can be unavailable (privacy mode); fall back to the default.
    return 'bottom-right';
  }
}

function storeCorner(corner: FabCorner) {
  try {
    window.localStorage.setItem(FAB_CORNER_STORAGE_KEY, corner);
  } catch {
    // Best-effort persistence; the in-session state is the source of truth.
  }
}

/** Which content-area corner the launcher is parked in, persisted per browser. */
export function useFabCorner() {
  const [corner, setCornerState] = useState<FabCorner>(readStoredCorner);

  const setCorner = (next: FabCorner) => {
    setCornerState(next);
    storeCorner(next);
  };

  return { corner, setCorner };
}
